import pkce from './pkce'
import store from 'store'
import EventEmitter from 'events'
import Popup from './popup'
import IFrame from './iframe'

class OAuth2 {
  constructor(id, params) {
    this.id = id
    this.key = `pls:oauth:${id}`
    this.ee = new EventEmitter()
    this.popup = new Popup()
    this.iframe = new IFrame()
    this.token = store.get(this.key)
  }

  destroy() {
    this.ee.removeAllListeners()
  }

  on (ev, cb) {
    this.ee.on(ev, cb)
  }
  
  async authorize(params, silent = false) {
    this.pkce = await pkce()
    const sp = this.toQueryParams({
      response_type: 'code',
      code_challenge: this.pkce.codeChallenge,
      code_challenge_method: 'S256',
      prompt: silent ? 'none' : undefined,
      ...params
    })
    
    try {
      const win = silent ? this.iframe : this.popup
      const popup = win.create(params.authorization_endpoint + '?' + sp, `popup-${this.id}`)
      const r = await win.monitorForHash()

      if(params.response_type === 'token') {
        return r
      } else {
        return r?.code
      }

    } catch(e) {
      console.log(e)
      if(silent) return this.authorize(params)
      throw e
    }
  }

  toQueryParams(params) {
    let sp = []
    for(let k in params) {
      if(params[k] === undefined) continue
      if(k.endsWith('_endpoint') || k === 'authority') continue
      sp.push(`${k}=${encodeURIComponent(params[k])}`)
    }
    return sp.join('&')
  }

  isExpired(token) {
    return token._exp - 15000 <= Date.now()
  }
  
  getToken(params) {
    const isExpired = this.token && this.isExpired(this.token)
    if(isExpired) {
      if(this.token.refresh_token) {
        this.promise = this._refreshToken(this.token.refresh_token, params)
      } else {
        this.promise = null
      }
      this.token = null
    }
    if(this.token) return this.token
    if(!this.promise) this.promise = this._getToken(params, isExpired)
    return this.promise
  }

  async _refreshToken(refresh_token, params) {
    const { token_endpoint, client_id } = params
    try {
      const result = await fetch(token_endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: this.toQueryParams({ refresh_token, client_id, grant_type: 'refresh_token' })
      })
      this.saveToken(await result.json())
    } catch(e) {
      this.saveToken(null)
      throw new Error('refresh token failed')
    }
    
    return this.token
  }
  
  async _getToken(params, silent = false) {
    params = {...params}
    let tok
    if(params.response_type === 'token') {
      tok = await this.authorize(params, silent)
    } else {
      params.code = await this.authorize(params, silent)
      params.grant_type = 'authorization_code'
      params.code_verifier = this.pkce.codeVerifier
      
      const result = await fetch(params.token_endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: this.toQueryParams(params)
      })
      tok = await result.json()
    }
    return this.saveToken(tok)
  }

  saveToken(token) {
    if(token?.access_token) {
      token._exp = Date.now() + token.expires_in * 1000
      token.token = [token.token_type, token.access_token].filter(Boolean).join(' ')
      store.set(this.key, token)
      this.token = token
      this.ee.emit('change', token)
    } else {
      this.token = null
      store.remove(this.key)
    }
    return this.token
  }
}

export default OAuth2
